import React, { useState } from 'react';
import { Grid, ListItemIcon, ListItemText, MenuItem } from '@mui/material';
import { useFormik, Form, FormikProvider } from 'formik';
import { DeleteConfirmationDialog, DeleteIcon, EntityEditDialog, SnackDisplay, TextInput } from '@lp/lp-ui';
import { RoleSchema } from '../../validationSchemes';
import { RoleModel } from '../../typings';
import { useCurrentUser } from '../../app/useCurrentUser';
import identityService from '../../services/IdentityService';
import { getBodyErrors } from '../../services/ApiService';
import { isSystemRole } from '../../constants';

type RoleFormModel = {
    name: string,
    description: string
};

type RoleDialogProps = {
    open: boolean,
    role: RoleModel | null, // null => create
    handleClose: (savedMessage?: string) => void
};

const RoleDialog: React.FC<RoleDialogProps> = ({ open, role, handleClose }: RoleDialogProps) => {
    const [, isTenantAdmin] = useCurrentUser();
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [snackMessage, setSnackMessage] = useState('');

    const isNew = !role || role.id === 0;
    const isSystem = !isNew && isSystemRole(role!.name);
    const canEdit = isTenantAdmin && !isSystem;

    const formik = useFormik<RoleFormModel>({
        initialValues: { name: role?.name ?? '', description: role?.description ?? '' },
        enableReinitialize: true,
        validationSchema: RoleSchema,
        onSubmit: async (values) => {
            const payload = { name: values.name.trim(), description: values.description?.trim() ?? '' };
            try {
                if (isNew) {
                    await identityService.createRole(payload);
                } else {
                    await identityService.updateRole(role!.id, payload);
                }
                handlePopupClose(`Role ${isNew ? 'created' : 'updated'} successfully.`);
                return true;
            } catch (err: any) {
                const status = err?.status;
                const bodyErrors = await getBodyErrors(err);
                const detail = bodyErrors[0]?.detail ?? 'Failed to save role.';
                if (status === 409) {
                    formik.setFieldError('name', detail);
                } else {
                    setSnackMessage(detail);
                }
                return false;
            }
        }
    });

    const { errors, touched, getFieldProps, submitForm } = formik;

    const handlePopupClose = (savedMessage?: string) => {
        formik.resetForm();
        setDeleteOpen(false);
        handleClose(savedMessage);
    };

    const handleDelete = async () => {
        setDeleteOpen(false);
        try {
            await identityService.deleteRole(role!.id);
            handlePopupClose('Role deleted successfully.');
        } catch (err: any) {
            const bodyErrors = await getBodyErrors(err);
            setSnackMessage(bodyErrors[0]?.detail ?? 'Failed to delete role.');
        }
    };

    return (
        <React.Fragment>
            <FormikProvider value={formik}>
                <Form autoComplete="off" noValidate onSubmit={submitForm}>
                    <EntityEditDialog
                        open={open}
                        editing={canEdit}
                        maxWidth="sm"
                        fullWidth
                        title={isNew ? 'New Role' : 'Role'}
                        handleClose={() => handlePopupClose()}
                        handleSave={submitForm}
                        showMoreMenu={!isNew && canEdit}
                        menuItems={[
                            <MenuItem key="role-delete" onClick={() => setDeleteOpen(true)}>
                                <ListItemIcon>
                                    <DeleteIcon />
                                </ListItemIcon>
                                <ListItemText>
                                    Delete
                                </ListItemText>
                            </MenuItem>
                        ]}
                    >
                        <Grid container spacing={1} columnSpacing={6}>
                            <Grid item xs={12}>
                                <TextInput
                                    isEditing={canEdit}
                                    required
                                    type="string"
                                    label="Name"
                                    {...getFieldProps('name')}
                                    error={Boolean(touched.name && errors.name)}
                                    helperText={touched.name && errors.name}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextInput
                                    isEditing={canEdit}
                                    type="string"
                                    label="Description"
                                    {...getFieldProps('description')}
                                    error={Boolean(touched.description && errors.description)}
                                    helperText={touched.description && errors.description}
                                />
                            </Grid>
                        </Grid>
                    </EntityEditDialog>
                </Form>
            </FormikProvider>

            <DeleteConfirmationDialog
                title="Are you sure you'd like to delete this role?"
                open={deleteOpen}
                handleClose={() => setDeleteOpen(false)}
                handleSave={handleDelete}
            />

            {snackMessage && (
                <SnackDisplay
                    snack={snackMessage}
                    color="error"
                    handleClose={() => setSnackMessage('')}
                />
            )}
        </React.Fragment>
    );
};

export default RoleDialog;
